// ETHRAEON Demos page · Phase S
// Tabbed demos with left explainer and right live panel.
// (c) S. Jason Prohaska (ingombrante) 2024-2026

import { useState } from "react";
import { DemoEmpty, DemoLoading } from "../components/DemoStates";
import {
  IconConstitutionalShell,
  IconDIM,
  IconRecursiveWitness,
  IconEthraeonTrinity,
} from "../components/icons";

const TABS = [
  {
    key: "shell",
    name: "Constitutional AI Shell",
    icon: <IconConstitutionalShell className="w-6 h-6" />,
    explainer:
      "The Constitutional AI Shell is a breathable boundary around model outputs. It helps keep decisions aligned with policy, context, and human agency.",
  },
  {
    key: "dim",
    name: "Decision Insight Model",
    icon: <IconDIM className="w-6 h-6" />,
    explainer:
      "The Decision Insight Model surfaces structured options without dictating outcomes. It clarifies choices and tradeoffs in plain language.",
  },
  {
    key: "witness",
    name: "Recursive Witness OS",
    icon: <IconRecursiveWitness className="w-6 h-6" />,
    explainer:
      "Recursive Witness OS provides traceability without surveillance. It reflects process steps back to the user so actions can be understood and reviewed.",
  },
  {
    key: "trinity",
    name: "Ethraeon Trinity",
    icon: <IconEthraeonTrinity className="w-6 h-6" />,
    explainer:
      "Three lenses, one intention. The Trinity composes Shell, DIM, and Witness into a single orchestration of calm decision support.",
  },
];

export function Demos() {
  const [active, setActive] = useState(TABS[0].key);
  const tab = TABS.find((t) => t.key === active)!;

  return (
    <main className="min-h-screen bg-white dark:bg-neutral-950 text-neutral-900 dark:text-neutral-50">
      <section className="max-w-6xl mx-auto px-6 py-16">
        <h1 className="text-3xl md:text-4xl font-light">Demos</h1>
        <p className="mt-3 text-neutral-600 dark:text-neutral-400">
          Calm, legible interfaces that show what each module does, with care.
        </p>

        <nav className="mt-8 flex flex-wrap gap-2 border-b border-neutral-200 dark:border-neutral-800 pb-2">
          {TABS.map((t) => (
            <button
              key={t.key}
              onClick={() => setActive(t.key)}
              className={`text-sm px-4 py-2 rounded-md border transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-neutral-400 ${
                active === t.key
                  ? "border-neutral-900 dark:border-neutral-100 bg-neutral-50 dark:bg-neutral-900"
                  : "border-transparent hover:bg-neutral-50 dark:hover:bg-neutral-900"
              }`}
            >
              {t.name}
            </button>
          ))}
        </nav>

        <div className="mt-8 grid grid-cols-1 lg:grid-cols-2 gap-8">
          <aside className="rounded-xl border border-neutral-200 dark:border-neutral-800 p-6">
            <div className="mb-4 text-neutral-700 dark:text-neutral-200">{tab.icon}</div>
            <h2 className="text-xl font-medium">{tab.name}</h2>
            <p className="mt-3 text-neutral-600 dark:text-neutral-400">{tab.explainer}</p>
          </aside>
          <section className="rounded-xl border border-neutral-200 dark:border-neutral-800 p-6 min-h-[280px]">
            {/* Live panel placeholder; calm empty by default */}
            <DemoEmpty message="This space is open for exploration." />
          </section>
        </div>
      </section>
    </main>
  );
}
